/**
 * Staffed-hours helpers.
 *
 * The offline state names the next staffed hour from the agency rota rather
 * than shrugging with a generic "we'll be back", so the merchant knows whether
 * to wait or email. Without a rota configured we assume someone is always on.
 *
 * Shape: { tz?: 'Europe/London', days: { 0..6: [startHour, endHour] } }
 * Days missing from the map are unstaffed.
 */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Local wall-clock parts in the rota's timezone, not the visitor's. */
function partsIn(date, timeZone) {
  if (!timeZone) return { day: date.getDay(), hour: date.getHours(), minute: date.getMinutes() };
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
  const index = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday);
  return {
    day: index === -1 ? date.getDay() : index,
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
  };
}

export function isWithinHours(hours, now = new Date()) {
  if (!hours?.days) return true;
  try {
    const { day, hour, minute } = partsIn(now, hours.tz);
    const window = hours.days[day] ?? hours.days[String(day)];
    if (!window) return false;
    const [start, end] = window;
    const decimal = hour + minute / 60;
    return decimal >= start && decimal < end;
  } catch {
    // A malformed rota must never make the widget claim to be closed.
    return true;
  }
}

/** "Back at 9:00 AM · about 7 hours" — or null when there is no rota. */
export function nextOpenLabel(hours, now = new Date()) {
  if (!hours?.days) return null;
  try {
    const { day, hour, minute } = partsIn(now, hours.tz);
    const decimal = hour + minute / 60;

    for (let offset = 0; offset < 8; offset += 1) {
      const probe = (day + offset) % 7;
      const window = hours.days[probe] ?? hours.days[String(probe)];
      if (!window) continue;
      const [start] = window;
      // Today only counts if the shift has not already started.
      if (offset === 0 && decimal >= start) continue;

      const hoursAway = offset * 24 + start - decimal;
      const when = offset === 0 ? '' : offset === 1 ? ' tomorrow' : ` ${DAY_NAMES[probe]}`;
      return {
        time: formatHour(start),
        when,
        away: hoursAway < 1 ? 'under an hour' : `about ${Math.round(hoursAway)} hours`,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function formatHour(value) {
  const hour = Math.floor(value);
  const minute = Math.round((value - hour) * 60);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${String(minute).padStart(2, '0')} ${suffix}`;
}

export default { isWithinHours, nextOpenLabel };
